import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";

export const Route = createFileRoute("/legal/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Terms governing use of The Hospitality Reserve platform connecting vetted independent bartenders with London's private venues.",
      },
      { property: "og:title", content: "Terms of Service — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/legal/terms" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/legal/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Terms of Service" updated="18 August 2026">
      <h2>1. Who we are</h2>
      <p>
        The Hospitality Reserve ("<strong>the Platform</strong>", "we", "us") is a
        software platform operated in England and Wales. The Platform provides
        technology that enables independent hospitality professionals
        ("<strong>Bartenders</strong>") and hospitality businesses
        ("<strong>Venues</strong>") to discover one another, agree commercial
        terms for a specific engagement, and process payment for that
        engagement.
      </p>

      <h2>2. We are not an employer or employment business</h2>
      <p>
        <strong>
          The Platform is a neutral technology intermediary only. It is not an
          employer, employment business, employment agency, or staffing
          provider.
        </strong>{" "}
        We do not supply labour, we do not direct or control how Bartenders
        perform services, we do not set working hours, and we do not own,
        operate, or supervise any Venue.
      </p>
      <ul>
        <li>
          Bartenders are <strong>self-employed independent contractors</strong>{" "}
          and contract directly with the Venue for each engagement.
        </li>
        <li>
          The contract for services formed on each booking is{" "}
          <strong>directly between the Bartender and the Venue</strong>. The
          Platform is not a party to that contract.
        </li>
        <li>
          The Platform does not fall within the scope of the Employment
          Agencies Act 1973 or the Conduct of Employment Agencies and
          Employment Businesses Regulations 2003, because it neither supplies
          workers to hirers nor introduces workers under those regulatory
          definitions in an employment-business capacity.
        </li>
        <li>
          Nothing in these Terms creates a relationship of employment, agency
          (in the legal sense), partnership, joint venture, or franchise
          between the Platform and any user.
        </li>
      </ul>

      <h2>3. Bartender responsibilities</h2>
      <p>By using the Platform as a Bartender, you confirm and agree that:</p>
      <ul>
        <li>You are 18 years of age or over.</li>
        <li>
          You have the legal right to work in the United Kingdom and will
          provide evidence on request.
        </li>
        <li>
          You are operating as a self-employed contractor and are solely
          responsible for your own income tax, National Insurance, VAT (if
          applicable), pension contributions, public liability insurance, and
          any other regulatory obligations.
        </li>
        <li>
          You will provide services with reasonable skill and care, hold any
          certifications you claim to hold, and comply with all licensing,
          health and safety, food hygiene, and alcohol-service obligations at
          the Venue.
        </li>
        <li>
          You accept or decline engagements at your sole discretion and can
          send a substitute only with the Venue's prior agreement.
        </li>
      </ul>

      <h2>4. Venue responsibilities</h2>
      <ul>
        <li>
          You are responsible for the premises, safe systems of work, on-site
          supervision, any required premises licences, and compliance with the
          Licensing Act 2003 and Health and Safety at Work etc. Act 1974.
        </li>
        <li>
          You accept that Bartenders engaged via the Platform are independent
          contractors and not your employees, workers, or agency workers.
        </li>
        <li>
          You agree to pay the agreed engagement fee, plus Platform service
          fees, through the Platform's payment processor.
        </li>
      </ul>

      <h2>5. Bookings and payment</h2>
      <p>
        Payments are processed by our regulated third-party payment provider.
        The Platform charges a service fee disclosed at checkout. Cancellation,
        refund, and dispute handling are governed by the cancellation policy
        shown at the point of booking, which forms part of these Terms.
      </p>
      <p>
        <strong>No fee for permanent employment.</strong> If a Venue offers, and
        a Bartender accepts, a permanent, fixed-term, or direct employment role
        after meeting through the Platform, no introduction fee, release fee,
        transfer fee, or commission is payable to the Platform. Platform service
        fees apply only to shifts booked through the Platform.
      </p>


      <h2>6. Shift termination for gross misconduct</h2>
      <p>
        A Venue manager or owner reserves the right to{" "}
        <strong>immediately terminate an engagement early</strong> where, acting
        reasonably, they consider the Bartender has committed an act of gross
        misconduct. Gross misconduct includes, but is not limited to:
      </p>
      <ul>
        <li>Theft, attempted theft, or dishonest handling of cash, stock, tips, or property;</li>
        <li>Physical violence, assault, or threats of violence toward any person;</li>
        <li>Verbal abuse, harassment, intimidation, or discriminatory conduct;</li>
        <li>Intentional or reckless damage to property or premises;</li>
        <li>
          Attending or working under the influence of alcohol or unlawful drugs,
          or consuming them while on shift;
        </li>
        <li>
          Any conduct that endangers the safety of guests, staff, or the public,
          or that breaches licensing law.
        </li>
      </ul>
      <p>
        Termination on these grounds must be recorded through the Platform at the
        time it occurs, with a written account of what happened and any available
        supporting evidence. The record is sent immediately to our team for
        review. Malicious, fabricated, or bad-faith terminations are themselves a
        breach of these Terms and may result in suspension of the Venue account
        and liability for the full engagement fee.
      </p>

      <h2>7. Payment for uncompleted hours and holding of funds</h2>
      <ul>
        <li>
          Where an engagement is terminated early for gross misconduct, the
          Bartender is eligible for payment{" "}
          <strong>only for hours actually worked up to the exact time of
          termination</strong>, calculated to the minute and subject to review.
          No payment is due for the unworked remainder of the engagement.
        </li>
        <li>
          The Platform will automatically prorate the engagement value to the
          recorded termination time. Amounts charged to the Venue are adjusted
          accordingly through our payment provider, and any pre-authorised amount
          in excess of the prorated total is released or refunded.
        </li>
        <li>
          <strong>
            The Platform reserves the right to hold, freeze, or withhold payout
            funds
          </strong>{" "}
          for the duration of an active dispute or investigation involving a
          severe policy violation, alleged illegal activity, or a safety
          incident. Funds are released, adjusted, or refunded once our
          investigation concludes.
        </li>
        <li>
          Where an investigation substantiates theft or fraud, the Platform may
          withhold payment in full and may refund the affected Venue.
        </li>
        <li>
          Either party may make representations during an investigation via{" "}
          <a href="mailto:admin@thehospitalityreserve.com">admin@thehospitalityreserve.com</a>.
          We aim to reach a decision within 5 working days of receiving the
          information we need.
        </li>
      </ul>

      <h2>8. Zero tolerance, permanent bans, and law enforcement</h2>
      <ul>
        <li>
          Any substantiated claim of <strong>physical violence, theft, or other
          illegal conduct</strong> will result in an{" "}
          <strong>immediate and permanent ban</strong> from the Platform. This
          applies to Bartenders and to Venue personnel alike.
        </li>
        <li>
          Accounts may be frozen without notice while a serious allegation is
          investigated, preventing further bookings, payouts, and messaging.
        </li>
        <li>
          A permanent ban also prohibits creating or using any further account on
          the Platform, whether directly or through another person or entity.
        </li>
        <li>
          <strong>
            The Hospitality Reserve reserves the right to cooperate fully with
            the police and other law enforcement or regulatory authorities
          </strong>{" "}
          and to disclose to them, on request or where we consider it necessary,
          identity verification details, right-to-work records, booking and
          payment records, messages, incident reports, and supporting evidence in
          connection with a criminal investigation or the prevention or detection
          of crime.
        </li>
        <li>
          Such disclosures are made under the lawful bases set out in our{" "}
          <a href="/legal/privacy">Privacy Policy</a> (legal obligation and
          legitimate interests, including the prevention and detection of crime).
        </li>
      </ul>

      <h2>9. Vetting</h2>
      <p>
        The Platform performs reasonable identity and right-to-work checks on
        Bartenders and may collect DBS information where appropriate. These
        checks are provided on an "as is" basis and do not constitute a
        guarantee or warranty by the Platform as to any individual's suitability.
        Venues remain responsible for their own due diligence.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, the Platform is not liable for
        any act, omission, default, or conduct of any Bartender or Venue, for
        the quality of services performed, for loss of profit, loss of business,
        or for any indirect or consequential loss. Our total aggregate liability
        in connection with the Platform shall not exceed the greater of (a) the
        service fees paid by you to the Platform in the 12 months preceding the
        claim, or (b) £100. Nothing in these Terms excludes liability for death
        or personal injury caused by negligence, fraud, or any liability that
        cannot be excluded by law.
      </p>

      <h2>11. Suspension and termination</h2>
      <p>
        We may suspend or terminate access at any time for breach of these
        Terms, the Acceptable Use Policy, or where reasonably necessary to
        protect the safety or integrity of the Platform.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. The courts
        of England and Wales have exclusive jurisdiction.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms: <a href="mailto:hello@thehospitalityreserve.com">hello@thehospitalityreserve.com</a>.
      </p>
    </LegalLayout>
  );
}
